import React from "react";
import {
	ClassNameProps,
	Column,
	If,
	makeClassName,
	makeObjectFromArrayMap,
	Row,
	StyleProps,
	toLocalDateString,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Table } from "@hex-insights/tables";
import { Icon } from "../../../../Components/Icon";
import {
	Contract,
	ContractLineItem,
	Discount,
	DiscountNameFragment,
	Service,
	ServiceNameFragment,
	useDiscountNamesQuery,
	useServiceNamesQuery,
} from "../../../../Utilities";
import { ContractLineItemFormat } from "../../../Format";
import { ContractLineItemFormValues } from "../../../FormUtilities";
import { ContractUtilities } from "../../../Utilities";
import { ContractLineItemCreateModalController } from "../CreateModal";
import { ControlledContractLineItemDetailModalController } from "../DetailModal";
import styles from "./styles.module.css";

const detailFormStateArgs = {
	editMode: EditMode.WriteOnly,
};

export type ContractLineItemsTableProps = {
	variant: "admin" | "parent";
	contract: Pick<Contract, "id" | "status"> | null;
	contractLineItems: (Pick<
		ContractLineItem,
		"id" | "displayOrder" | "isDiscount" | "amount" | "dateAdded" | "dateRemoved"
	> & {
		service: (Pick<Service, "id"> & ServiceNameFragment) | null;
		discount: (Pick<Discount, "id"> & DiscountNameFragment) | null;
		childContractLineItems: Pick<ContractLineItem, "id" | "isDiscount" | "amount">[];
	})[];
	noCreate?: boolean;
	noUpdate?: boolean;
	discountLabel?: string;
} & Partial<ClassNameProps & StyleProps>;

export function ContractLineItemsTable({
	variant,
	contract,
	contractLineItems,
	noCreate = false,
	noUpdate = false,
	discountLabel = "Discount",
	className,
	style,
}: ContractLineItemsTableProps) {
	const { contractLineItemPrices, totalPrice } = React.useMemo(() => {
		const contractLineItemPrices: Record<ContractLineItem["id"], { price: number; discount: number; total: number }> =
			{};
		let totalPrice = 0;

		for (let i = 0; i < contractLineItems.length; i++) {
			const lineItem = contractLineItems[i];
			const price = lineItem.amount ?? 0;
			const discount = lineItem.childContractLineItems.reduce((a, e) => a + (e.amount ?? 0), 0);
			const total = price - discount;

			contractLineItemPrices[lineItem.id] = { price, discount, total };
			totalPrice += total;
		}

		return { contractLineItemPrices, totalPrice };
	}, [contractLineItems]);

	const createFormStateArgs = React.useMemo(() => {
		const displayOrder = Math.max(...contractLineItems.map((e) => e.displayOrder), contractLineItems.length);

		return {
			initialFormValues: {
				displayOrder,
				contractID: contract?.id ?? null,
				dateAdded: contract?.status === ContractUtilities.ContractStatus.Draft ? null : toLocalDateString(new Date()),
			},
		};
	}, [contract, contractLineItems]);

	const allowsUpdate = variant === "admin" && !noUpdate;
	const allowsCreate = variant === "admin" && !noCreate;

	const { columns, columnWidths } = React.useMemo(() => {
		const columns = ["displayOrder", "service", "price", "discount", "total"];
		const columnWidths = { displayOrder: "2.5rem" };
		if (allowsUpdate) {
			columns.push("update");
			columnWidths["update"] = "3rem";
		}
		return { columns, columnWidths };
	}, [allowsUpdate]);

	return (
		<Table
			columns={columns}
			columnWidths={columnWidths}
			className={makeClassName(styles["table"], className)}
			style={style}
		>
			<Table.Header className={styles["table__header"]}>
				<Table.Row>
					<Table.Heading column="displayOrder" noResize></Table.Heading>
					<Table.Heading column="service" noResize>
						Service
					</Table.Heading>
					<Table.Heading column="price" noResize>
						Price
					</Table.Heading>
					<Table.Heading column="discount" noResize>
						{discountLabel}
					</Table.Heading>
					<Table.Heading column="total" noResize>
						Total
					</Table.Heading>
					<If condition={allowsUpdate}>
						<Table.Heading column="update" noResize></Table.Heading>
					</If>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{contractLineItems.map((lineItem, index) => {
					const { price, discount, total } = contractLineItemPrices[lineItem.id];

					return (
						<Table.Row key={index} className={styles["table__row"]}>
							<Table.Cell column="displayOrder" oneLine numeric>
								{index + 1}
							</Table.Cell>
							<Table.Cell column="service">
								<Column justify="spaced-start" verticalSpacing="0.25rem">
									<TruncatedText>{ContractLineItemFormat.name(lineItem)}</TruncatedText>
									<If condition={!!lineItem.dateAdded}>
										<span className={styles["contract-line-item__added-note"]}>
											Added {ContractLineItemFormat.Fields.dateAdded(lineItem.dateAdded)}
										</span>
									</If>
									<If condition={!!lineItem.dateRemoved}>
										<span className={styles["contract-line-item__removed-note"]}>
											Removed {ContractLineItemFormat.Fields.dateRemoved(lineItem.dateRemoved)}
										</span>
									</If>
								</Column>
							</Table.Cell>
							<Table.Cell column="price" oneLine numeric>
								{ContractLineItemFormat.Fields.amount(price)}
							</Table.Cell>
							<Table.Cell column="discount" oneLine numeric>
								{ContractLineItemFormat.Fields.amount(discount)}
							</Table.Cell>
							<Table.Cell column="total" oneLine numeric>
								<strong>{ContractLineItemFormat.Fields.amount(total)}</strong>
							</Table.Cell>
							<If condition={allowsUpdate}>
								<Table.Cell column="update" oneLine>
									<ControlledContractLineItemDetailModalController
										variant="link"
										size="small"
										contractLineItemID={lineItem.id}
										formStateArgs={detailFormStateArgs}
									>
										Edit
									</ControlledContractLineItemDetailModalController>
								</Table.Cell>
							</If>
						</Table.Row>
					);
				})}
				<If condition={allowsCreate}>
					<Table.Row>
						<Table.Cell column={columns[0]} colspan={columns.length}>
							<ContractLineItemCreateModalController
								variant="link"
								size="small"
								formStateArgs={createFormStateArgs}
								formNameSuffix={contract?.id}
							>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Plus size={16} /> Add Service
								</Row>
							</ContractLineItemCreateModalController>
						</Table.Cell>
					</Table.Row>
				</If>
			</Table.Body>
			<Table.Footer className={styles["table__footer"]}>
				<Table.Row>
					<Table.Cell column="displayOrder" oneLine></Table.Cell>
					<Table.Cell column="service" oneLine>
						<strong>Total</strong>
					</Table.Cell>
					<Table.Cell column="price" oneLine></Table.Cell>
					<Table.Cell column="discount" oneLine></Table.Cell>
					<Table.Cell column="total" oneLine numeric>
						<strong>{ContractLineItemFormat.Fields.amount(totalPrice)}</strong>
					</Table.Cell>
				</Table.Row>
			</Table.Footer>
		</Table>
	);
}

export type ContractLineItemsFormTableProps = Omit<
	ContractLineItemsTableProps,
	"contract" | "noCreate" | "contractLineItems"
> & {
	formValuesSets: Pick<
		ContractLineItemFormValues.Create,
		"displayOrder" | "serviceID" | "discountID" | "amount" | "childContractLineItems"
	>[];
};

export function ContractLineItemsFormTable({ formValuesSets, variant, ...props }: ContractLineItemsFormTableProps) {
	const { serviceIDs, discountIDs } = React.useMemo(() => {
		const serviceIDs: Service["id"][] = [];
		const discountIDs: Discount["id"][] = [];
		for (const formValues of formValuesSets) {
			if (formValues.serviceID) {
				serviceIDs.push(formValues.serviceID);
			}
			if (formValues.discountID) {
				discountIDs.push(formValues.discountID);
			}
			for (const childFormValues of formValues.childContractLineItems) {
				if (childFormValues.serviceID) {
					serviceIDs.push(childFormValues.serviceID);
				}
				if (childFormValues.discountID) {
					discountIDs.push(childFormValues.discountID);
				}
			}
		}
		return { serviceIDs, discountIDs };
	}, [formValuesSets]);

	const { data: servicesData } = useServiceNamesQuery({
		variables: { filters: { idIn: serviceIDs } },
		skip: serviceIDs.length === 0,
	});
	const serviceMap = React.useMemo(
		() => makeObjectFromArrayMap(servicesData?.serviceConnection.edges ?? [], ({ node }) => [node.id, node]),
		[servicesData],
	);

	const { data: discountsData } = useDiscountNamesQuery({
		variables: { filters: { idIn: discountIDs } },
		skip: discountIDs.length === 0,
	});
	const discountMap = React.useMemo(
		() => makeObjectFromArrayMap(discountsData?.discountConnection.edges ?? [], ({ node }) => [node.id, node]),
		[discountsData],
	);

	const contractLineItems = React.useMemo(() => {
		function formValuesToLineItem(
			formValues: Pick<ContractLineItemFormValues.Base, "displayOrder" | "serviceID" | "discountID" | "amount">,
			i: number,
		) {
			return {
				id: i.toString(),
				displayOrder: formValues.displayOrder ?? 0,
				isDiscount: !!formValues.discountID,
				amount: formValues.amount ?? 0,
				dateAdded: null,
				dateRemoved: null,
				service: formValues.serviceID ? serviceMap[formValues.serviceID] ?? { id: `s-${i}`, name: "Loading..." } : null,
				discount: formValues.discountID
					? discountMap[formValues.discountID] ?? { id: `d-${i}`, name: "Loading..." }
					: null,
			};
		}

		return formValuesSets.map((formValues, i) => ({
			...formValuesToLineItem(formValues, i),
			childContractLineItems: formValues.childContractLineItems.map((formValues, i) =>
				formValuesToLineItem(formValues, i),
			),
		}));
	}, [formValuesSets, serviceMap, discountMap]);

	return (
		<ContractLineItemsTable
			variant={variant}
			contract={null}
			noCreate
			contractLineItems={contractLineItems}
			{...props}
		/>
	);
}
